/**
 * NewRegistrationsCard — بطاقة «معدل تسجيل العملاء الجدد» (v200.93).
 *
 * قرار المالك 1b+2a: تقرأ حصراً من GET /api/customers/new-registrations
 * (جدول customer_first_seen المحسوب مسبقاً) — لا حساب في المتصفح ولا datasets.
 * التصميم يلتزم برموز مركز العملاء الموحّد (نفس لوحة teal/emerald والخطوط).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { api } from "../lib/api";
import { useCustomerFilter } from "./CustomerFilterContext";
import { ksaDaysAgo } from "../lib/ksaTime";

const TEAL = "#065F46";
const TEAL_SOFT = "#0F766E";
const BORDER = "1px solid rgba(6,95,70,0.20)";

function _isoDaysAgo(days) {
  return ksaDaysAgo(days);
}

const _lbl = { fontSize: 10, color: TEAL_SOFT, fontWeight: 700 };
const _val = { marginTop: 2, fontWeight: 600, fontSize: 12, color: TEAL };
const _input = {
  fontSize: 13, padding: "6px 10px", borderRadius: 10,
  border: BORDER, background: "#fff", color: TEAL, fontWeight: 600,
};

export const NewRegistrationsCard = () => {
  // Phase 7h — تستهلك فلتر الشريط الموحّد حصريًا (حُذفت أدوات التحكم
  // المحلية والتجاوز المحلي نهائيًا). لا مصدر تاريخ/فرع بديل.
  const shared = useCustomerFilter();
  const start = shared?.from || "";
  const end = shared?.to || "";
  const branchEff = shared?.branchId || "";
  const granularity = "daily";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setErr(false);
    return api
      .get("/customers/new-registrations", {
        params: { start, end, granularity, branch_id: branchEff || undefined },
      })
      .then((r) => setData(r.data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, [start, end, granularity, branchEff]);

  useEffect(() => { load(); }, [load]);

  const branches = data?.branches || [];
  const chartData = useMemo(
    () => (data?.series || []).map((s) => ({ period: s.period, count: s.count })),
    [data]
  );
  const cov = data?.coverage || {};

  return (
    <section
      data-testid="new-reg-card"
      dir="rtl"
      style={{
        marginTop: 24, padding: "18px 20px", borderRadius: 16,
        background: "#fff", border: BORDER,
        boxShadow: "0 1px 3px rgba(6,95,70,0.06)",
        fontVariantNumeric: "lining-nums tabular-nums",
      }}
    >
      {/* ── العنوان */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: TEAL }}>
          🌱 معدل تسجيل العملاء الجدد
        </h3>
        <span style={{ fontSize: 12, color: TEAL_SOFT }}>
          عدد العملاء حسب أول ظهور لهم (أول فاتورة فعلية) عبر الوقت
        </span>
      </div>

      {/* ── الرقم الإجمالي */}
      <div style={{ marginTop: 18, display: "flex", alignItems: "baseline", gap: 10 }}>
        <span data-testid="new-reg-total" style={{
          fontSize: 40, fontWeight: 900, color: TEAL, lineHeight: 1,
          fontFamily: "monospace",
        }}>
          {loading ? "…" : (data?.total ?? 0).toLocaleString("en-US")}
        </span>
        <span style={{ fontSize: 13, color: TEAL_SOFT, fontWeight: 600 }}>
          عميل جديد خلال الفترة المختارة
        </span>
      </div>

      {/* ── الرسم البياني */}
      <div data-testid="new-reg-chart" style={{ marginTop: 14, height: 260, width: "100%" }}>
        {err ? (
          <div style={{ color: "#B91C1C", fontSize: 13, padding: 20 }}>
            تعذّر تحميل البيانات — أعد المحاولة
            <button data-testid="new-reg-retry" onClick={load}
              style={{ ..._input, marginInlineStart: 10, cursor: "pointer" }}>
              إعادة المحاولة
            </button>
          </div>
        ) : chartData.length === 0 ? (
          <div style={{ color: TEAL_SOFT, fontSize: 13, padding: 20 }}>
            لا تسجيلات جديدة ضمن هذه الفترة.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 12, left: -8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(6,95,70,0.12)" />
              <XAxis dataKey="period" tick={{ fontSize: 10, fill: TEAL_SOFT }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: TEAL_SOFT }} />
              <Tooltip
                formatter={(v) => [v, "عملاء جدد"]}
                contentStyle={{ fontSize: 12, borderRadius: 10, border: BORDER }}
              />
              <Bar dataKey="count" fill={TEAL} radius={[6, 6, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── شعار الشروط: المصدر + النافذة + التغطية (نمط دليل العملاء) */}
      <section
        data-testid="new-reg-conditions"
        style={{
          marginTop: 16, padding: "12px 16px", borderRadius: 14,
          background: "linear-gradient(to left, #F0F9FF, #ECFDF5)",
          border: BORDER, display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: 12,
        }}
      >
        <div>
          <div style={_lbl}>المصدر</div>
          <div data-testid="new-reg-source" style={{ ..._val, fontFamily: "monospace", fontSize: 11 }}>
            {data?.source?.collection || "customer_first_seen"} · محدَّث تلقائياً
          </div>
        </div>
        <div>
          <div style={_lbl}>نافذة القياس</div>
          <div data-testid="new-reg-window" style={_val}>
            {data?.start} ← {data?.end} · {granularity === "daily" ? "يومي" : "شهري"}
          </div>
        </div>
        <div>
          <div style={_lbl}>بلا هاتف صالح (ضمن الفترة)</div>
          <div data-testid="new-reg-coverage" style={_val}>
            {(cov.invoices_without_phone ?? 0).toLocaleString("en-US")} فاتورة
            {" "}من {(cov.invoices_total ?? 0).toLocaleString("en-US")} في الفترة
          </div>
        </div>
        <div>
          <div style={_lbl}>فقد هوية كامل (بلا اسم وبلا هاتف)</div>
          <div data-testid="new-reg-full-loss" style={_val}>
            {(cov.full_identity_loss ?? 0).toLocaleString("en-US")} فاتورة ·
            {" "}{(cov.full_identity_loss_pct ?? 0).toLocaleString("en-US")}% من إجمالي الفترة
          </div>
        </div>
        <div>
          <div style={_lbl}>إجمالي العملاء (كل الوقت)</div>
          <div data-testid="new-reg-registry-total" style={_val}>
            {(cov.customers_total ?? 0).toLocaleString("en-US")} عميلًا في السجلّ
          </div>
        </div>
        <div>
          <div style={_lbl}>دلالة الفرع</div>
          <div style={_val}>أول ظهور للمنصة الذي حدث في الفرع المحدد</div>
        </div>
      </section>
    </section>
  );
};

export default NewRegistrationsCard;
